import { useEffect } from 'react';

import {
  DEFAULT_DESCRIPTION,
  SITE_NAME,
  documentTitleFor,
} from '../utils/routeMeta';

/**
 * Lightweight per-route meta updater. Sets <title>, meta description,
 * canonical, and Open Graph + Twitter tags. No external dependency.
 *
 * Every value it writes comes from data/routeMeta.json by way of
 * utils/routeMeta, which scripts/generate-target-artifacts.js also reads when
 * it builds the static route shells. That is what lets the head a crawler
 * receives before any JavaScript runs match the head this hook produces after
 * hydration (Release 2.0.1, task R02).
 */
function setMetaTag({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (name)     el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLinkRel(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * `canonicalUrl` is always an absolute parent-site URL, supplied by
 * utils/routeMeta. It used to be derived from window.location.origin unless a
 * page overrode it, which meant a parent-site page self-canonicalised to
 * whichever host served it. Taking it from the route map instead is what makes
 * the static shell and the hydrated head identical on all three hosts, and it
 * keeps the D-02 rule that a page served on an app subdomain still declares
 * https://tickleapps.com/apps/<brand>/... as its canonical URL.
 */
export default function useDocumentMeta({ title, description, canonicalUrl, ogType = 'website', ogImage }) {
  useEffect(() => {
    const fullTitle = documentTitleFor(title);
    const desc = description || DEFAULT_DESCRIPTION;
    const url  = canonicalUrl;

    document.title = fullTitle;
    setMetaTag({ name: 'description', content: desc });
    setLinkRel('canonical', url);

    // Open Graph
    setMetaTag({ property: 'og:title',       content: fullTitle });
    setMetaTag({ property: 'og:description', content: desc });
    setMetaTag({ property: 'og:type',        content: ogType });
    setMetaTag({ property: 'og:url',         content: url });
    setMetaTag({ property: 'og:site_name',   content: SITE_NAME });
    if (ogImage) setMetaTag({ property: 'og:image', content: ogImage });

    // Twitter
    setMetaTag({ name: 'twitter:card',        content: ogImage ? 'summary_large_image' : 'summary' });
    setMetaTag({ name: 'twitter:title',       content: fullTitle });
    setMetaTag({ name: 'twitter:description', content: desc });
    setMetaTag({ name: 'twitter:url',         content: url });
    if (ogImage) setMetaTag({ name: 'twitter:image', content: ogImage });
  }, [title, description, canonicalUrl, ogType, ogImage]);
}
